import { startOfDay } from "date-fns";

export type DatePickerField = {
  value?: Date;
  onChange: (_value: Date | undefined) => void;
  onBlur?: () => void;
  name?: string;
};

export type DatePickerErrorDisplay = "inline" | "form";

export interface FormDatePickerProps {
  field: DatePickerField;
  allowOlderDates?: boolean;
  minDate?: Date;
  maxDate?: Date;
  initialDate?: Date;
  disabled?: boolean;
  /** inline = show error below input; form = delegate to onValidationError / FormMessage */
  errorDisplay?: DatePickerErrorDisplay;
  onValidationError?: (_message: string | null) => void;
  /** Links input to external error element (e.g. FormMessage id from useFormField) */
  ariaDescribedBy?: string;
}

export interface DateConstraintOptions {
  allowOlderDates?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

export function createIsDateDisabled({
  allowOlderDates,
  minDate,
  maxDate,
}: DateConstraintOptions) {
  return (date: Date) => {
    // Compare calendar days, not exact instants.
    const normalizedDate = startOfDay(date);
    const today = startOfDay(new Date());
    if (!allowOlderDates && normalizedDate < today) return true;
    if (minDate && normalizedDate < startOfDay(minDate)) return true;
    if (maxDate && normalizedDate > startOfDay(maxDate)) return true;
    return false;
  };
}

export function getOutOfRangeMessage(
  t: (_key: string) => string,
  date: Date,
  options: DateConstraintOptions
): string | null {
  const isDateDisabled = createIsDateDisabled(options);
  if (!isDateDisabled(date)) return null;

  const today = startOfDay(new Date());
  if (!options.allowOlderDates && startOfDay(date) < today) {
    return t("dateinput.mustbefuture");
  }

  return t("dateinput.outofrange");
}
